import { Empty, Table, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import type { StockState } from '../../domain/metals';
import { metalCategoryLabelKey, type MetalCategory } from '../../domain/metals/metalCategories';
import {
    formatStockNumber,
    GRAMS_DIGITS,
    MONEY_DIGITS,
    overallStockFigures,
    stockMetalShares,
    totalStockByMetal,
    type StockMetalTotal,
    type StockTableRow,
} from '../../domain/metals/stockSummary';
import StockFiguresCard from './StockFiguresCard';
import StockMetalBar from './StockMetalBar';

const { Text } = Typography;

export interface StockContentProps {
    t: (key: string) => string;
    stockData: StockState[];
    /** The latest BNR rate, RON per EUR; `null` shows the EUR figures as unknown. */
    exchangeRate: number | null;
}

/**
 * A fineness shows its title and percentage; an inventory under it shows its date after
 * "Inventory", or after "Purchased" when it is a purchase.
 */
const renderLabel = (t: StockContentProps['t'], row: StockTableRow) => {
    if (row.kind === 'fineness') {
        return (
            <>
                {formatStockNumber(row.label, 1)} <Tag>{row.percent}</Tag>
            </>
        );
    }
    const day = dayjs(row.label).format('YYYY-MM-DD');
    const originKey = row.purchaseId === null ? 'metals.stockInventory' : 'metals.stockPurchased';
    return <Text type="secondary">{`${t(originKey)} ${day}`}</Text>;
};

const numberRender = (digits: number) => (value: number) => formatStockNumber(value, digits);

const numberColumn = (key: keyof StockTableRow, title: string, digits: number) => ({
    key,
    title,
    dataIndex: key,
    align: 'right' as const,
    render: numberRender(digits),
});

const renderTotal = (t: StockContentProps['t'], total: StockMetalTotal) => (
    <Table.Summary.Row>
        <Table.Summary.Cell index={0}>
            <Text strong>{t('metals.total')}</Text>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={1} align="right">
            <Text strong>{formatStockNumber(total.quantity, GRAMS_DIGITS)}</Text>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={2} align="right">
            <Text strong>{formatStockNumber(total.purifiedQuantity, GRAMS_DIGITS)}</Text>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={3} align="right">
            <Text strong>{formatStockNumber(total.avgPrice, GRAMS_DIGITS)}</Text>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={4} align="right">
            <Text strong>{formatStockNumber(total.valueRon, MONEY_DIGITS)}</Text>
        </Table.Summary.Cell>
    </Table.Summary.Row>
);

const StockContent = ({ t, stockData, exchangeRate }: StockContentProps) => {
    const totals = totalStockByMetal(stockData, exchangeRate);

    if (totals.length === 0) {
        return <Empty data-testid="stock-empty" description={t('metals.noStock')} />;
    }

    const columns = [
        {
            key: 'label',
            title: t('metals.karat'),
            dataIndex: 'label',
            render: (_label: string, row: StockTableRow) => renderLabel(t, row),
        },
        numberColumn('quantity', t('metals.quantity'), GRAMS_DIGITS),
        numberColumn('purifiedQuantity', t('metals.gr24k'), GRAMS_DIGITS),
        numberColumn('avgPrice', t('metals.pmp'), GRAMS_DIGITS),
        numberColumn('valueRon', t('metals.valueRon'), MONEY_DIGITS),
    ];

    return (
        <div>
            <StockFiguresCard
                t={t}
                testId="stock-overall"
                title={<Text strong>{t('metals.stockSummary.overall')}</Text>}
                figures={overallStockFigures(totals, exchangeRate)}
                kind="gold"
                pmpLabelKey="metals.stockSummary.goldPmp"
                aside={<StockMetalBar t={t} shares={stockMetalShares(totals)} />}
            />
            {totals.map(total => (
                <div key={total.metalType} data-testid={`stock-${total.metalType}`}>
                    <StockFiguresCard
                        t={t}
                        testId={`stock-figures-${total.metalType}`}
                        title={
                            <Text strong style={{ fontSize: 13 }}>
                                {t(metalCategoryLabelKey(total.metalType as MetalCategory))}
                            </Text>
                        }
                        figures={total}
                        kind={total.kind}
                    />
                    <Table
                        columns={columns}
                        dataSource={total.rows}
                        rowKey="key"
                        pagination={false}
                        size="small"
                        summary={() => renderTotal(t, total)}
                        style={{ marginBottom: 12 }}
                    />
                </div>
            ))}
        </div>
    );
};

export default StockContent;
